import React, { ComponentProps, memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react'

import { pxToRem } from '@/shared/styles/themes'
/* eslint-disable @typescript-eslint/no-explicit-any */
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart'
import { styled, useMediaQuery, useTheme } from '@mui/material'

import FlickityComponent from '../Flickity/Flickity'
import { ProductCarouselContext } from './ProductCarouselContext'
import { StyledCarouselButton, StyledHeartIcon } from './StyledButton'

const MemoisedFlickity = memo(FlickityComponent)

export type ProductCarouselImageSliderProps = ComponentProps<typeof FlickityComponent> & {
  children: React.ReactNode
  onLike?: () => void
  isLiked?: boolean
}

export const ProductCarouselImageSlider: React.FC<ProductCarouselImageSliderProps> = ({ children, isLiked, onLike, ...props }) => {
  const theme = useTheme()
  const isTablet = useMediaQuery(theme.breakpoints.up('sm'))
  const { flickityRef, setFlickityActive } = useContext(ProductCarouselContext)
  const sliderRef = useRef<HTMLDivElement>(null)

  const assignFlickityRef = useCallback((carouselRef: any) => {
    flickityRef.current = carouselRef
    setFlickityActive(true)
  }, [flickityRef, setFlickityActive])

  // Custom page dots for mobile devices
  useEffect(() => {
    if (!isTablet && flickityRef && flickityRef.current) {
      const updateDotVisibility = (index: number) => {
        const dots = document.querySelectorAll('.flickity-page-dots .dot')
        const dotsContainer = document.querySelector('.flickity-page-dots')

        if (dotsContainer && dots.length > 0) {
          dots.forEach(dot => {
            dot.classList.remove('medium')
            dot.classList.remove('small')
          })

          if (index < 3) {
            dots?.[3].classList.add('medium')
            dots?.[4].classList.add('small')
          } else if (index >= dots.length - 3) {
            dots[dots.length - 5].classList.add('small')
            dots[dots.length - 4].classList.add('medium')
          } else {
            dots[index - 1].classList.add('medium')
            dots[index + 1].classList.add('medium')
            dots[index - 2].classList.add('small')
            dots[index + 2].classList.add('small')
          }

          // Scroll dots to centered
          const dotWidth = 26
          const newScrollLeft = dotWidth * (index - 2)

          dotsContainer.scrollLeft = newScrollLeft
        }
      }

      flickityRef.current.on('change', updateDotVisibility)

      return () => {
        flickityRef.current.off('change', updateDotVisibility)
      }
    }
  }, [flickityRef, isTablet])

  useEffect(() => {
    if (!isTablet && sliderRef.current) {
      const element = sliderRef.current
      let startX = 0
      let startY = 0
      let isHorizontalSwipe = false
      
      const handleTouchStart = (e: TouchEvent) => {
        startX = e.touches[0].clientX
        startY = e.touches[0].clientY
        isHorizontalSwipe = false
      }
      
      const handleTouchMove = (e: TouchEvent) => {
        if (!e.touches[0]) return
        
        const deltaX = Math.abs(e.touches[0].clientX - startX)
        const deltaY = Math.abs(e.touches[0].clientY - startY)
        
        // If horizontal movement is greater than vertical and exceeds threshold
        if (deltaX > deltaY && deltaX > 8) {
          isHorizontalSwipe = true
        }
        
        // Prevent default scrolling if determined to be a horizontal swipe
        if (isHorizontalSwipe) {
          e.preventDefault()
        }
      }
      
      element.addEventListener('touchstart', handleTouchStart, { passive: true })
      element.addEventListener('touchmove', handleTouchMove, { passive: false })
      
      return () => {
        element.removeEventListener('touchstart', handleTouchStart)
        element.removeEventListener('touchmove', handleTouchMove)
      }
    }
  }, [isTablet])

  const options = useMemo(() => ({
    freeScroll: false,
    setGallerySize: false,
    wrapAround: true,
    draggable: !isTablet,
    imagesLoaded: true,
    arrowShape: 'M70.8332 6.63231e-05C71.6572 0.000224235 72.4626 0.219647 73.1477 0.630596C73.8327 1.04155 74.3666 1.62557 74.6819 2.30884C74.9972 2.99211 75.0797 3.74394 74.919 4.46929C74.7583 5.19464 74.3616 5.86095 73.7791 6.38397L39.7291 36.9459C37.3857 39.0499 36.0692 41.9031 36.0692 44.8781C36.0692 47.8531 37.3857 50.7063 39.7291 52.8103L73.7791 83.3723C74.5381 84.0776 74.958 85.0223 74.9486 86.0029C74.9391 86.9834 74.5009 87.9214 73.7283 88.6148C72.9558 89.3082 71.9107 89.7015 70.8182 89.7101C69.7258 89.7186 68.6733 89.3416 67.8874 88.6604L33.8374 58.0984C29.9369 54.5894 27.7463 49.835 27.7463 44.8781C27.7463 39.9213 29.9369 35.1668 33.8374 31.6578L67.8874 1.09584C68.6686 0.394432 69.7283 0.000278138 70.8332 6.63231e-05Z',
  }), [isTablet])

  return (
    <StyledGrid ref={sliderRef}>
      <StyledFlickityComponent
        flickityRef={assignFlickityRef}
        className="carousel"
        options={options}
        static
        {...props}
      >
        {children}
      </StyledFlickityComponent>
      <StyledCarouselButton
        onClick={onLike}
        aria-label="Like"
      >
        <StyledHeartIcon
          $active={isLiked}
          width={28}
          height={28}
          icon={faHeart}
        />
      </StyledCarouselButton>
    </StyledGrid>
  )
}

export const StyledGrid = styled('div')`
  position: relative;
  width: 100%;

  ${props => props.theme.breakpoints.up('sm')} {
    width: calc(100dvw - ${pxToRem(148)});
  }

  ${props => props.theme.breakpoints.up('md')} {
    width: calc(60dvw - ${pxToRem(96)});
    max-width: ${pxToRem(760)};
  }
`

export const StyledFlickityComponent = styled(MemoisedFlickity)`
  width: 100%;
  height: ${pxToRem(380)};
  position: relative;
  outline: none;

  .flickity-viewport {
    &:focus {
      outline: 0 !important;
    }

    ${props => props.theme.breakpoints.up('sm')} {
      &::before {
        left: calc(50% - 50vw);
        background: ${props => props.$hasOverlay ? 'linear-gradient(to left, rgba(255, 255, 255, 0), #f8f8f8)' : 'rgba(255, 255, 255, 0)'};
      }

      &::after {
        right: calc(50% - 50vw);
        background: ${props => props.$hasOverlay ? 'linear-gradient(to right, rgba(255, 255, 255, 0), #f8f8f8)' : 'rgba(255, 255, 255, 0)'};
      }
    }
  }
  
  .flickity-page-dots {
    bottom: ${pxToRem(24)};
    width: 130px;
    overflow-x: scroll;
    position: relative;
    margin: auto;
    display: inline-block;
    white-space: nowrap;
    scroll-behavior: smooth;
    margin-left: calc(50% - 65px);
    
    ::-webkit-scrollbar {
      display: none;
    }
  
    -ms-overflow-style: none;
    scrollbar-width: none;

    .small {
      width: 6px;
      height: 6px;
      margin: 0 10px;
    }

    .medium {
      width: 8px;
      height: 8px;
      margin: 0 9px;
    }
  }

  .flickity-button {
    display: none;
  }

  ${props => props.theme.breakpoints.up('sm')} {
    height: ${pxToRem(560, 0.8)};
    
    .flickity-page-dots {
      display: none;
    }

    .flickity-button {
      display: unset;
      width: ${pxToRem(64)};
      height: ${pxToRem(64)};
      box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
      color: ${props => props.theme.palette.text.primary};
    }

    .previous svg {
      width: 40%;
      height: 40%;
      left: 28%;
      top: 29%;
    }
  
    .next svg {
      width: 40%;
      height: 40%;
      left: 33%;
      top: 29%;
    }
  }
`
